"use client";

import { ReactNode, useEffect, useRef, useState } from "react";

interface LazySectionProps {
  children: ReactNode;
  /** Approximate height placeholder to prevent CLS when the section hasn't loaded yet */
  minHeight?: string;
  /** Additional class names for the placeholder */
  className?: string;
  /** How far before the viewport to start loading (IntersectionObserver rootMargin) */
  rootMargin?: string;
}

/**
 * Defers mounting of children until the section scrolls near the viewport.
 * This prevents below-the-fold sections from hydrating and executing JS
 * during the critical LCP window, reducing Total Blocking Time.
 */
export default function LazySection({
  children,
  minHeight = "400px",
  className = "",
  rootMargin = "200px 0px",
}: LazySectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div ref={ref} className={className}>
      {isVisible ? children : <div style={{ minHeight }} />}
    </div>
  );
}
